import { useState, useEffect, useCallback } from 'react';
import { ENV_CONFIG } from '../config';
import WebSocketService from '../services/websocket';
import LiffService from '../services/liff';
import DebugLiffService from '../services/debugLiff';

const useWebSocket = (isLiffReady) => {
  const [wsConnected, setWsConnected] = useState(false);
  const [wsError, setWsError] = useState(null);
  const [messages, setMessages] = useState([]);

  const isDebugMode = ENV_CONFIG.DEBUG;

  /**
   * 現在のIDトークンを取得するヘルパー関数。
   * WebSocketリトライ時にトークンを最新化するために使用。
   */
  const getCurrentIdToken = useCallback(() => {
    try {
      if (isDebugMode) {
        return DebugLiffService.getIDToken();
      } else {
        return LiffService.getIDToken();
      }
    } catch (error) {
      console.error('IDトークン取得エラー:', error);
      return null;
    }
  }, [isDebugMode]);

  useEffect(() => {
    if (!isLiffReady) return;

    // リトライ時に最新トークンを取得できるようコールバックを設定
    WebSocketService.setTokenGetter(getCurrentIdToken);

    const connectWebSocket = async () => {
      try {
        const idToken = getCurrentIdToken();

        if (!idToken) {
          setWsError('IDトークンが取得できませんでした');
          return;
        }

        await WebSocketService.connect(idToken);

      } catch (error) {
        console.error('WebSocket接続エラー:', error);
        setWsError(error.message);
      }
    };

    connectWebSocket();

    // WebSocketのステータス変更を監視
    const unsubscribeStatus = WebSocketService.onStatusChange((connected) => {
      setWsConnected(connected);

      if (isDebugMode) {
        console.log('デバッグモード WebSocket状態変更:', connected ? '接続' : '切断');
      }
    });

    // WebSocketのメッセージを監視
    const unsubscribeMessage = WebSocketService.onMessage((message) => {
      setMessages(prev => [...prev, message]);

      if (isDebugMode) {
        console.log('デバッグモード WebSocketメッセージ:', message);
      }
    });

    // WebSocketの認証エラーを監視
    const unsubscribeAuthError = WebSocketService.onAuthError((code, reason) => {
      console.error('WebSocket認証エラー:', { code, reason });
      setWsError('認証エラーが発生しました。画面を再読み込みしてください。');
    });

    return () => {
      unsubscribeStatus();
      unsubscribeMessage();
      unsubscribeAuthError();
      WebSocketService.setTokenGetter(null);
      WebSocketService.disconnect();
    };
  }, [isLiffReady, isDebugMode, getCurrentIdToken]);

  const sendMessage = useCallback((message) => {
    if (WebSocketService.isConnected) {
      WebSocketService.socket.send(JSON.stringify(message));

      if (isDebugMode) {
        console.log('デバッグモード WebSocketメッセージ送信:', message);
      }
    } else {
      console.warn('WebSocket is not connected');
    }
  }, [isDebugMode]);

  return {
    wsConnected,
    wsError,
    messages,
    sendMessage,
    isDebugMode
  };
};

export default useWebSocket;